import { useEffect, useState } from "react";
import { api, formatApiError, fmtDate, fmtMoney } from "@/lib/api";
import { generatePdfBlob } from "@/lib/pdfExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LockOpen, Check, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LABELS = {
    statements_uploaded: "Statements uploaded for every active card",
    expenses_entered: "All expenses entered",
    receipts_uploaded: "Receipts uploaded",
    reconciled: "Statements reconciled",
    payments_made: "Payments made",
    proofs_uploaded: "Payment proofs uploaded",
    payments_reflected: "Payments reflected in bank/card",
    disputes_noted: "Disputes noted",
    late_fees_checked: "Late fees checked",
    interest_checked: "Interest charges checked",
    gst_checked: "GST charges checked",
    reports_exported: "Reports exported",
};

export default function MonthlyClosing() {
    const { hasRole } = useAuth();
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [closing, setClosing] = useState(null);
    const [summary, setSummary] = useState(null);
    const [list, setList] = useState([]);
    const [remarks, setRemarks] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [c, s, l] = await Promise.all([
            api.get(`/monthly-closing/${month}`),
            api.get(`/monthly-closing/${month}/summary`),
            api.get("/monthly-closing"),
        ]);
        setClosing(c.data);
        setSummary(s.data);
        setList(l.data);
        setRemarks(c.data.remarks || "");
    };
    useEffect(() => { load(); }, [month]);

    const toggle = (k) => {
        if (closing?.status === "Closed") return;
        setClosing((prev) => ({ ...prev, checklist: { ...prev.checklist, [k]: !prev.checklist?.[k] } }));
    };

    const save = async () => {
        setBusy(true);
        try {
            await api.post("/monthly-closing", { month, checklist: closing.checklist, remarks });
            toast.success("Progress saved");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const closeMonth = async () => {
        if (!window.confirm(`Close ${month}? Editing will be blocked until admin reopens it.\n\nAfter close, the Monthly Expense PDF is emailed automatically to the finance team.`)) return;
        try {
            await api.post(`/monthly-closing/${month}/close`);
            toast.success("Month closed · Sending report…");
            // Fire the email in the background so it doesn't block UI
            emailReport(month);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    /** Build the same branded PDF as `/reports → Monthly Expense` and POST it
     *  to the backend for Resend delivery to admin+finance users. */
    const emailReport = async (targetMonth) => {
        try {
            const [cardsRes, rowsRes] = await Promise.all([
                api.get("/cards"),
                api.get("/reports/monthly-spend", { params: { month: targetMonth } }),
            ]);
            const cards = cardsRes.data || [];
            const rows = rowsRes.data || [];
            const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));
            const cols = [
                { label: "Date", value: (r) => fmtDate(r.date) },
                { label: "Merchant", value: "merchant" },
                { label: "Platform", value: (r) => r.platform || "—" },
                { label: "Card", value: (r) => { const c = cardMap[r.card_id]; return c ? `${c.bank_name} ${c.card_name} ••••${c.last_4}` : ""; } },
                { label: "Category", value: "category" },
                { label: "B/P", value: "business_personal" },
                { label: "Gross", value: "gross_amount", money: true },
                { label: "On Card", value: "amount", money: true },
            ];
            const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
            const gross = rows.reduce((s, r) => s + Number(r.gross_amount || r.amount || 0), 0);
            const { base64, filename } = generatePdfBlob({
                title: "Monthly Expense",
                filters: { Month: targetMonth, Card: "All cards", "Business/Personal": "All" },
                summary: `${rows.length} entries · Card total ${fmtMoney(total)}${gross > total ? ` · saved ${fmtMoney(gross - total)}` : ""}`,
                columns: cols,
                rows,
                filename: `Monthly_Expense_${targetMonth}`,
            });
            const res = await api.post(`/monthly-closing/${targetMonth}/email-report`, {
                pdf_base64: base64,
                filename,
            });
            if (res.data?.sent) {
                toast.success(`Emailed to ${res.data.recipients?.length || 0} recipient(s)`);
            } else {
                toast.message("Email queued", { description: res.data?.note || "Report attached to audit log." });
            }
        } catch (err) {
            toast.error(`Email failed: ${formatApiError(err)}`);
        }
    };

    const resendReport = async () => {
        if (!window.confirm(`Resend the ${month} Monthly Expense PDF to the finance team?`)) return;
        toast.message("Preparing PDF…");
        await emailReport(month);
    };

    const [reopenReason, setReopenReason] = useState("");
    const [reopenOpen, setReopenOpen] = useState(false);

    const reopen = async () => {
        if (reopenReason.trim().length < 5) {
            toast.error("Please write at least 5 characters explaining why.");
            return;
        }
        try {
            await api.post(`/monthly-closing/${month}/reopen`, { reason: reopenReason.trim() });
            toast.success("Month reopened · audit trail updated");
            setReopenOpen(false);
            setReopenReason("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!closing) return <div className="text-stone-500">Loading…</div>;

    const allDone = closing.checklist && Object.values(closing.checklist).every(Boolean);
    const doneCount = closing.checklist ? Object.values(closing.checklist).filter(Boolean).length : 0;
    const total = closing.checklist ? Object.keys(closing.checklist).length : 12;
    const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

    return (
        <div data-testid="closing-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Monthly Closing</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Close the books.</h1>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <div className="text-sm text-stone-500">
                            {doneCount}/{total} checklist items done · Status <span className="font-medium">{closing.status}</span>
                        </div>
                        {/* Closing progress % */}
                        <div className="flex items-center gap-2" data-testid="closing-score">
                            <div className="w-40 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-500 ${
                                        pct >= 100
                                            ? "bg-emerald-500"
                                            : pct >= 75
                                              ? "bg-blue-500"
                                              : pct >= 50
                                                ? "bg-amber-500"
                                                : "bg-red-500"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <span className={`text-xs font-medium tabular-nums ${
                                pct >= 100
                                    ? "text-emerald-700"
                                    : pct >= 75
                                      ? "text-blue-700"
                                      : pct >= 50
                                        ? "text-amber-700"
                                        : "text-red-700"
                            }`}>
                                {pct}% complete
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="closing-month" />
                    {closing.status === "Closed" ? (
                        <>
                            {hasRole("admin", "finance") && (
                                <Button onClick={resendReport} variant="outline" data-testid="closing-resend-btn">
                                    <Mail size={16} className="mr-1.5" /> Resend report
                                </Button>
                            )}
                            {hasRole("admin") && (
                                <Button onClick={() => setReopenOpen(true)} variant="outline" data-testid="closing-reopen">
                                    <LockOpen size={16} className="mr-1.5" /> Reopen
                                </Button>
                            )}
                        </>
                    ) : (
                        hasRole("admin", "finance") && (
                            <>
                                <Button onClick={save} disabled={busy} variant="outline" data-testid="closing-save">
                                    {busy ? "Saving…" : "Save progress"}
                                </Button>
                                <Button onClick={closeMonth} disabled={!allDone} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="closing-close-btn">
                                    <Lock size={16} className="mr-1.5" /> {allDone ? "Close month · Email PDF" : "Complete checklist first"}
                                </Button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* Summary */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="kpi-card"><div className="eyebrow">Active cards</div><div className="font-display text-2xl mt-2 tabular-nums">{summary.active_cards}</div></div>
                    <div className="kpi-card"><div className="eyebrow">Statements uploaded</div><div className="font-display text-2xl mt-2 tabular-nums">{summary.statements_uploaded}</div></div>
                    <div className="kpi-card"><div className="eyebrow">Expenses entered</div><div className="font-display text-2xl mt-2 tabular-nums">{summary.expenses_entered}</div></div>
                    <div className="kpi-card"><div className="eyebrow">Payments recorded</div><div className="font-display text-2xl mt-2 tabular-nums">{summary.payments_recorded}</div></div>
                </div>
            )}

            {/* Checklist */}
            <div className="form-section">
                <div className="eyebrow mb-4">Checklist</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.keys(LABELS).map((k) => {
                        const checked = !!closing.checklist?.[k];
                        return (
                            <label key={k} onClick={() => toggle(k)} className={`flex items-center gap-3 border rounded-md px-3 py-3 cursor-pointer transition-colors ${checked ? "border-emerald-200 bg-emerald-50" : "border-stone-200"} ${closing.status === "Closed" ? "cursor-not-allowed opacity-70" : ""}`}>
                                <Checkbox checked={checked} data-testid={`chk-${k}`} />
                                <span className={`text-sm ${checked ? "text-stone-900" : "text-stone-600"}`}>{LABELS[k]}</span>
                                {checked && <Check size={14} className="text-emerald-700 ml-auto" />}
                            </label>
                        );
                    })}
                </div>
                <div className="mt-6">
                    <Label className="eyebrow">Remarks</Label>
                    <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5" disabled={closing.status === "Closed"} />
                </div>
            </div>

            {/* History */}
            <div className="mt-8">
                <div className="eyebrow mb-3">Closing history</div>
                <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Month</th><th>Status</th><th>Closed by</th><th>Closed at</th><th>Remarks</th></tr>
                        </thead>
                        <tbody>
                            {list.map((c) => (
                                <tr key={c.id}>
                                    <td className="tabular-nums">{c.month}</td>
                                    <td>
                                        <span className={`status-pill ${c.status === "Closed" ? "bg-stone-100 text-stone-700 border-stone-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status}</span>
                                    </td>
                                    <td>{c.closed_by_name || "—"}</td>
                                    <td className="tabular-nums text-xs">{c.closed_at ? fmtDate(c.closed_at) : "—"}</td>
                                    <td className="text-xs max-w-[300px] truncate">{c.remarks || "—"}</td>
                                </tr>
                            ))}
                            {list.length === 0 && <tr><td colSpan={5} className="text-center text-stone-500 py-8">No closings yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Reopen dialog — reason required for audit trail */}
            <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
                <DialogContent className="max-w-md" data-testid="reopen-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">Reopen {month}?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-stone-600">
                            Reopening a closed month allows edits to statements, expenses and payments for
                            that period. Your reason will be logged to the audit trail and shown to any
                            future admin who opens this month.
                        </p>
                        <div>
                            <Label className="eyebrow">Reason (min 5 chars)</Label>
                            <Textarea
                                rows={3}
                                value={reopenReason}
                                onChange={(e) => setReopenReason(e.target.value)}
                                placeholder="e.g. Late invoice from Amex arrived after close — need to add expenses & re-run reconciliation."
                                className="mt-1.5"
                                data-testid="reopen-reason-input"
                            />
                            <div className="text-[11px] text-stone-500 mt-1">
                                {reopenReason.trim().length}/500 characters
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setReopenOpen(false); setReopenReason(""); }} data-testid="reopen-cancel-btn">
                            Cancel
                        </Button>
                        <Button
                            onClick={reopen}
                            disabled={reopenReason.trim().length < 5}
                            className="bg-stone-900 hover:bg-stone-800 text-white"
                            data-testid="reopen-confirm-btn"
                        >
                            <LockOpen size={14} className="mr-1.5" /> Confirm reopen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reopen history — shown only for months that have been reopened */}
            {closing.reopen_history && closing.reopen_history.length > 0 && (
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="reopen-history">
                    <div className="eyebrow text-amber-800 mb-2">Reopen audit trail · {closing.reopen_history.length} event(s)</div>
                    <ul className="space-y-2">
                        {closing.reopen_history.map((h, i) => (
                            <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                                <LockOpen size={13} className="mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <div>{h.reason}</div>
                                    <div className="text-[11px] text-amber-800/70 mt-0.5">
                                        by {h.reopened_by_name || "admin"} · {fmtDate(h.reopened_at)}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
